import * as React from 'react';
// @ts-expect-error
import {useFieldArray} from 'react-hook-form';
// @ts-expect-error
import type {Control, Watch} from 'react-hook-form';
import {connect} from 'react-redux';

import type {FormFieldSubfieldsInput as Subfield} from 'nutshell-graphql-types';

import type {FormFieldSchema} from '../../../core/nutshell-fields/types';

import type {NutshellState} from '../../../../store';

import {fetchSchemaAction} from '../../forms-actions';
import {getFormSchema} from '../../forms-selectors';
import {getFieldMappingLabel, isInputType} from '../../helpers';
import type {ActiveField} from '../../types';

import {FieldDesignForm} from './design/field-details-forms/field-design-form';
import {FormDesignForm} from './design/form-design-form';
import {updateRemainingPersonEmailFields, updateExistingPersonEmailFields} from './helpers';
import {FieldMapping} from './mapping/field-mapping';
import {FieldMappingPlaceholder} from './mapping/field-mapping-placeholder';
import {Editor} from './preview/editor';

import styles from './editor-styles.module.css';

type StateProps = {
    formSchema: FormFieldSchema;
};

type DispatchProps = {
    fetchFormSchema: () => void;
};

type Props = StateProps &
    DispatchProps & {
        formId: string;
        formHeader?: React.ReactNode;
        control: Control;
        watch: Watch;
        postAddField: () => Promise<any>;
        saveFailedErrorMessage: string | null | undefined;
        onClearError: () => void;
        disableDesign?: boolean;
        disableHeaderFields?: boolean;
        minimumOneEmailField?: boolean;
    };

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        fetchFormSchema: () => dispatch(fetchSchemaAction()),
    };
}

function mapStateToProps(state: NutshellState): StateProps {
    return {
        formSchema: getFormSchema(state),
    };
}

function FormEditorComponent(props: Props) {
    const [activeField, setActiveField] = React.useState<ActiveField | null | undefined>(null);

    const {control, watch} = props;

    const {fields, append, remove, move, update} = useFieldArray({
        control,
        name: 'fields',
        keyName: 'key',
    });

    const submitButton = watch('submitButton');

    const onRemoveField = (index: number) => {
        // if the form requires at least one person email address and this deletion will
        // leave only one person email field, we need to mark it not removable and required
        if (
            props.minimumOneEmailField &&
            fields[index].fieldName === 'email' &&
            fields[index].entityType === 'CONTACT'
        ) {
            updateRemainingPersonEmailFields(index, fields, update);
        }

        setActiveField(null);
        remove(index);
    };

    const onEditNameField = (index: number, updatedFieldName: string) => {
        const oldField = fields[index];
        const updatedLabel = getFieldMappingLabel(props.formSchema, updatedFieldName, 'CONTACT');
        // @ts-expect-error
        const updatedPlaceholder = props.formSchema.properties[updatedFieldName].placeholder;
        const updatedField = {
            ...oldField,
            fieldName: updatedFieldName,
            label: updatedLabel,
            placeholder: updatedPlaceholder,
        } as const;

        update(index, updatedField);
    };

    // update the address fields, either the toggle between single and multi fields or the fields themselves
    const onEditAddressMultiFields = (
        index: number,
        updatedAddressFields?: Subfield[],
        isSeparateFields?: boolean
    ) => {
        const oldField = watch(`fields.${index}`);
        const updatedField = {
            ...oldField,
            ...(updatedAddressFields && {subfields: updatedAddressFields}),
            ...(isSeparateFields !== undefined && {isSeparateFields: isSeparateFields}),
        } as const;

        update(index, updatedField);
    };

    const onEditEmailField = (index: number, updatedEntityName: string) => {
        const oldField = fields[index];

        // This updates the isRemovable flag on all email fields on the form
        if (oldField.fieldName === 'email' && props.minimumOneEmailField) {
            if (updatedEntityName === 'CONTACT') {
                updateExistingPersonEmailFields(fields, update);
            } else if (updatedEntityName === 'ACCOUNT') {
                updateRemainingPersonEmailFields(index, fields, update);
            }
        }
    };

    const activeFieldIndex =
        activeField && (!submitButton || activeField.id !== submitButton.id)
            ? // @ts-expect-error
              fields.findIndex((field) => {
                  return field.id === activeField.id;
              })
            : undefined;

    const activeFieldValue =
        typeof activeFieldIndex === 'number' ? fields[activeFieldIndex] : undefined;

    // @ts-expect-error
    const fieldDesignForms = fields.map((field, index) => {
        return (
            <FieldDesignForm
                key={field.key}
                field={field}
                name={`fields.${index}`}
                control={control}
                onDeselectField={() => setActiveField(null)}
                watch={watch}
                title={getFormName(field.type)}
            />
        );
    });

    // FormSchema comes from an async request, gotta make sure
    // it's loaded before  trying to do anything
    if (!props.formSchema) {
        return null;
    }

    const getDesignForm = () => {
        if (props.disableDesign) {
            return null;
        }

        if (!activeField) {
            return <FormDesignForm control={control} />;
        }

        if (activeFieldValue) {
            return fieldDesignForms[activeFieldIndex];
        }

        if (activeField && submitButton && activeField.id === submitButton.id) {
            return (
                <FieldDesignForm
                    field={submitButton}
                    name='submitButton'
                    control={control}
                    onDeselectField={() => setActiveField(null)}
                    watch={watch}
                    title='Button design'
                />
            );
        }
    };

    const getFieldMappingComponent = () => {
        if (!activeField && props.disableDesign) {
            return <FieldMappingPlaceholder />;
        }

        if (
            typeof activeFieldIndex === 'number' &&
            activeFieldValue &&
            isInputType(activeFieldValue.type)
        ) {
            return (
                <FieldMapping
                    field={activeFieldValue}
                    fields={fields}
                    otherFieldsOnForm={fields
                        .slice(0, activeFieldIndex)
                        .concat(fields.slice(activeFieldIndex + 1))
                        // @ts-expect-error
                        .filter((field) => field.type === 'INPUT')}
                    name={`fields.${activeFieldIndex}`}
                    control={control}
                    watch={watch}
                    formSchema={props.formSchema}
                    onEditNameField={(updatedFieldName: string) =>
                        onEditNameField(activeFieldIndex, updatedFieldName)
                    }
                    onEditEmailField={(updatedEntityName: string) =>
                        onEditEmailField(activeFieldIndex, updatedEntityName)
                    }
                    onEditAddressMultiFields={(
                        updatedFields?: Subfield[],
                        updatedIsSeparateFields?: boolean
                    ) =>
                        onEditAddressMultiFields(
                            activeFieldIndex,
                            updatedFields,
                            updatedIsSeparateFields
                        )
                    }
                    preventEditing={!activeFieldValue.isRemovable}
                    formId={props.formId}
                />
            );
        }
    };

    return (
        <div className={styles['outer-container']}>
            <div className={styles['left-container']}>
                <Editor
                    formHeader={props.formHeader}
                    formId={props.formId}
                    fields={fields}
                    control={control}
                    watch={watch}
                    append={append}
                    update={update}
                    onRemoveField={onRemoveField}
                    move={move}
                    activeField={activeField}
                    setActiveField={(input?: ActiveField | null) => setActiveField(input)}
                    postAddField={props.postAddField}
                    saveFailedErrorMessage={props.saveFailedErrorMessage}
                    onClearError={props.onClearError}
                    formSchema={props.formSchema}
                    fetchFormSchema={props.fetchFormSchema}
                    disableHeaderFields={props.disableHeaderFields}
                    minimumOneEmailField={props.minimumOneEmailField}
                />
            </div>
            <div className={styles['right-container']}>
                {getFieldMappingComponent()}
                {getDesignForm()}
            </div>
        </div>
    );
}

// @ts-expect-error
const connector = connect<Props, Record<any, any>, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const FormEditor = connector(FormEditorComponent);

const getFormName = (type: string) => {
    switch (type) {
        case 'HEADER':
            return 'Header design';
        case 'SUBHEADER':
            return 'Subheader design';
        case 'TEXT':
            return 'Text design';
        case 'IMAGE':
            return 'Image design';
        default:
            return 'Field design';
    }
};
